import { useQuery } from "@tanstack/react-query";
import { useNavigate, useLocation } from "react-router-dom";
import { clearAuthStorage } from "@/utils";

/**
 * React Query-backed replacement for usePostData.
 * Caches POST responses by [url, requestBody] key.
 *
 * Returns [data, isLoaded, error, refetch, isFetching].
 *   - isLoaded: false only on the very first fetch (no cached data yet). Safe to
 *     gate loading spinners on this — background refetches leave it true.
 *   - isFetching: true whenever any network request is in-flight, including
 *     background refetches. Use this for subtle "refreshing" indicators only.
 */
const usePostQuery = (url, requestBody, options = {}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const queryFn = async () => {
    const headers = {
      "Content-Type": "application/json",
      ...options.headers,
    };

    const response = await fetch(url, {
      method: "POST",
      headers,
      credentials: "include",
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      if (response.status === 401) {
        clearAuthStorage();
        localStorage.setItem(
          "redirectAfterLogin",
          location.pathname + location.search,
        );
        navigate("/login");
        return null;
      }

      const errorData = await response.json().catch(() => null);
      const errorMessage =
        errorData?.message || `HTTP error! Status: ${response.status}`;
      throw new Error(errorMessage);
    }

    const response_json = await response.json();
    return response_json["data"];
  };

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: [url, requestBody],
    queryFn,
    enabled: !options.skip && !!url && requestBody != null,
    staleTime: options.staleTime ?? 5 * 60 * 1000, // default: 5 minutes
  });

  // `isLoaded` is true as soon as data has ever been fetched (i.e. the cache
  // slot is populated). Basing it on `data !== undefined` rather than
  // `isSuccess` means a background-refetch failure never flips it back to
  // false — React Query keeps the last-good cached value in `data` even when
  // the latest fetch throws, so components keep rendering that cached data
  // instead of reverting to a full loading spinner.
  const isLoaded = !isLoading && data !== undefined;

  return [data ?? [], isLoaded, error ?? null, refetch, isFetching];
};

export default usePostQuery;
